import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const COLORS = ["var(--color-cyan)", "var(--color-mint)", "var(--color-pink)", "var(--color-lilac)"];

/**
 * A body in the middle, the rest going round it.
 *
 * The going round is the content, so unlike every other figure this one does
 * not stop. It is also the reason the periods are not equal: the outer rings
 * take longer, which is the thing a child is usually asking about when they ask
 * why a year on Mars is longer than a year here.
 *
 * Rotated with CSS rather than SVG's animateMotion, so `prefers-reduced-motion`
 * can actually switch it off — the cycle's dot had to be hidden outright
 * because SMIL ignores the media query.
 *
 * Names sit in a key underneath rather than on the picture. Concentric rings
 * share a top, so labels placed on them stacked into a vertical list; moved
 * round the rings instead, the outer ones ran off the edge and none of them
 * could be matched to a moving dot. A key is unambiguous, holds still, and
 * costs one line.
 */
export function OrbitFigure({ figure }: FigureProps<Figure>) {
  const labels = figure.labels ?? [];
  if (labels.length < 2) return null;

  const [centre, ...bodies] = labels;
  const size = 260;
  const mid = size / 2;
  const step = bodies.length > 0 ? (mid - 34) / bodies.length : 0;

  return (
    <div role="img" aria-label={`${bodies.join(" and ")} going round ${centre}.`}>
      <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto block h-auto w-full max-w-[280px]">
        {bodies.map((body, i) => {
          const radius = 34 + step * i + step / 2;
          // Further out, slower. Not Kepler's third law — just its direction,
          // which is the part being explained.
          const seconds = 7 + i * 4.5;
          return (
            <g key={body}>
              <circle
                cx={mid}
                cy={mid}
                r={radius}
                fill="none"
                stroke="rgb(183 155 255 / 0.28)"
                strokeWidth="1.5"
                strokeDasharray="4 6"
              />
              <g
                className="orbiting"
                style={{
                  transformOrigin: `${mid}px ${mid}px`,
                  animationDuration: `${seconds}s`,
                  /*
                    A negative delay starts the animation partway through, so
                    the bodies begin spread around their rings. Without it they
                    all start at three o'clock in a straight line, which looks
                    like an alignment the picture is claiming rather than an
                    accident of when it loaded.
                  */
                  animationDelay: `${-seconds * (0.13 + i * 0.29)}s`,
                }}
              >
                <circle cx={mid + radius} cy={mid} r="9" fill={COLORS[i % COLORS.length]} />
              </g>
            </g>
          );
        })}

        <circle cx={mid} cy={mid} r="26" fill="var(--color-gold)" />
        <text
          x={mid}
          y={mid + 4}
          textAnchor="middle"
          fontSize="11"
          fontWeight="800"
          fill="var(--color-night)"
        >
          {centre}
        </text>
      </svg>

      <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {bodies.map((body, i) => (
          <span key={body} className="flex items-center gap-1.5 text-[13px] text-cream/80">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: COLORS[i % COLORS.length] }}
            />
            {body}
          </span>
        ))}
      </div>
    </div>
  );
}
